// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: simplified Glass product popup.
//
// Single-screen form with horizontal labels matching the screenshot
// reference. 2-column grid (Col md={12} pairs) on desktop; collapses
// to single-column on mobile.
//
// Maps onto inventory_items + item_glass_specs via
// simpleCreateHelpers.buildPayloadsFor('glass', values).
// ─────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { Checkbox, Col, Form, Input, InputNumber, Select } from 'antd';
import SimpleCreateModalShell from './SimpleCreateModalShell';
import { SIMPLE_KINDS, GLASS_CATEGORY_OPTIONS, ITEM_TYPE_OPTIONS, TAXABILITY_OPTIONS } from '../../simpleCreateConstants';
import { ItemCategorySelect, UomSelect, PolishTypeSelect, BrandSelect, GstRateSelect, StockItemSelect } from './simpleCreateFields';
import TripleSalesRateInput from './TripleSalesRateInput';
import { studioLookups } from '../../itemsApi';
// Stage 6B.8A-3N — Category dropdown is now DB-driven. Fetched
// rows fall back to the frozen GLASS_CATEGORY_OPTIONS seeds when
// the API is unreachable, so the popup never lands empty.
import { loadGlassCategoryLookupsSafe } from '../../../glassCategories/glassCategoryApi';

const GST_RULE = {
  validator: (_, v) =>
    v == null || v === '' || (Number(v) >= 0 && Number(v) <= 100)
      ? Promise.resolve()
      : Promise.reject(new Error('GST must be between 0 and 100'))
};
const PRICE_RULE = {
  validator: (_, v) =>
    v == null || v === '' || Number(v) >= 0
      ? Promise.resolve()
      : Promise.reject(new Error('Selling Price cannot be negative'))
};
const THICKNESS_RULE = {
  validator: (_, v) => {
    if (v == null || v === '') return Promise.resolve();
    const num = Number(v);
    if (!Number.isFinite(num)) {
      return Promise.reject(new Error('Thickness must be a number'));
    }
    if (num < 0) {
      return Promise.reject(new Error('Thickness cannot be negative'));
    }
    // Defensive — reject more than 2 decimals at submit time even though
    // the InputNumber parser already strips extra digits during typing.
    const decimals = String(v).split('.')[1];
    if (decimals && decimals.length > 2) {
      return Promise.reject(new Error('Thickness allows up to 2 decimal places'));
    }
    return Promise.resolve();
  }
};

// Strip everything that isn't a digit or a single decimal point and clamp
// the fractional part to 2 chars. Runs on every keystroke inside the AntD
// InputNumber, so letters / symbols / negative signs simply never reach
// the bound form value — the user cannot type them in the first place.
const TWO_DECIMAL_PARSER = (raw) => {
  if (raw == null) return '';
  const cleaned = String(raw).replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot < 0) return cleaned;
  // Keep only the first dot; drop later ones.
  const intPart  = cleaned.slice(0, dot);
  const fracPart = cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  return fracPart.length ? `${intPart}.${fracPart}` : `${intPart}.`;
};

export default function CreateGlassModal({
  open, onClose, onSaved, editRecord = null, initialValues: initialValuesProp = null,
  viewOnly = false
}) {
  // Live category list from the new Glass Category Master.
  //
  // Fallback semantics matter:
  //   • API unreachable (network / 5xx) → loadGlassCategoryLookupsSafe
  //     returns null → we KEEP the frozen GLASS_CATEGORY_OPTIONS seeds
  //     so the dropdown stays usable offline.
  //   • API responds, even with an empty list → we REPLACE the seeds
  //     with whatever the master holds (including []). An operator who
  //     intentionally cleared the master must NOT see stale seeds.
  const [categoryOptions, setCategoryOptions] = useState(GLASS_CATEGORY_OPTIONS);
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      const out = await loadGlassCategoryLookupsSafe();
      if (cancelled) return;
      if (out === null) return;  // API unreachable — keep seeds.
      const rows = Array.isArray(out.rows) ? out.rows : [];
      // Normalise to the legacy {label, glassFamily, glassColor} shape
      // so downstream helpers (splitGlassPayload, hydrateGlass) stay
      // unchanged. Falls back gracefully if a row is missing a field.
      const live = rows
        .filter((r) => r && r.categoryName)
        .map((r) => ({
          label:       r.categoryName,
          glassFamily: r.glassFamily || 'other',
          glassColor:  r.glassColor  || r.categoryName
        }));
      setCategoryOptions(live);  // may be [] — empty master shows empty.
    })();
    return () => { cancelled = true; };
  }, [open]);

  const initialValues = (editRecord || viewOnly) && initialValuesProp
    ? { ...initialValuesProp,
        _stockMode: initialValuesProp.sourceStockItemId ? 'derived' : 'self' }
    : { taxabilityType: 'taxable', salesRate: 0, _stockMode: 'self' };
  return (
    <SimpleCreateModalShell
      kind={SIMPLE_KINDS.GLASS}
      open={open}
      onClose={onClose}
      onSaved={onSaved}
      editRecord={editRecord}
      viewOnly={viewOnly}
      initialValues={initialValues}
      render={({ form }) => (
        <>
          {/* Row 1 — Product Name (75%) + Polish Type (25%) */}
          <Col xs={24} md={18}>
            <Form.Item
              label="Product Name"
              name="itemName"
              required
              rules={[{ required: true, message: 'Product Name is required' }]}
            >
              <Input placeholder="e.g., Clear Float Glass" autoFocus />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              label="Polish"
              name="polishTypeId"
              labelCol={{ flex: '52px' }}
              wrapperCol={{ flex: 'auto' }}
              rules={[{ required: true, message: 'Polish is required' }]}
            >
              <PolishTypeSelect placeholder="Pick polish…" />
            </Form.Item>
          </Col>

          {/* Row 2 — Stock Name (75%) + Is Stock toggle (25%) */}
          <Form.Item name="sourceStockItemId" hidden><Input /></Form.Item>
          <Form.Item name="stockDesc"         hidden><Input /></Form.Item>
          <Form.Item name="_stockMode"        hidden><Input /></Form.Item>
          <Col xs={24} md={18}>
            <Form.Item
              label="Stock Name"
              required
              tooltip="Pick an existing base stock item or type a free-text description."
            >
              <StockItemSelect
                mode={Form.useWatch('_stockMode', form) || 'self'}
                pickedId={Form.useWatch('sourceStockItemId', form)}
                textValue={Form.useWatch('stockDesc', form)}
                productName={Form.useWatch('itemName', form)}
                excludeId={editRecord && editRecord.id}
                onPick={(id) => form.setFieldValue('sourceStockItemId', id)}
                onChangeText={(text) => form.setFieldValue('stockDesc', text)}
                placeholder="Pick a root stock (e.g., 4MM CLEAR GLASS)"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6} style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Checkbox
              checked={(Form.useWatch('_stockMode', form) || 'self') === 'self'}
              onChange={(e) => {
                const checked = e.target.checked;
                form.setFieldValue('_stockMode', checked ? 'self' : 'derived');
                if (checked) {
                  form.setFieldValue('sourceStockItemId', null);
                  form.setFieldValue('stockDesc', form.getFieldValue('itemName') || '');
                } else {
                  form.setFieldValue('stockDesc', '');
                }
              }}
            >
              Is Stock?
            </Checkbox>
          </Col>

          {/* Row 3 — Label Name (full width, required) */}
          <Col xs={24}>
            <Form.Item label="Label Name" name="labelDesc"
              rules={[{ required: true, message: 'Label Name is required' }]}>
              <Input placeholder="Display label name" />
            </Form.Item>
          </Col>

          {/* Row 4 — Thickness + Brand (both required) */}
          <Col xs={24} md={12}>
            <Form.Item label="Thick (mm)" name="thicknessMm"
              rules={[{ required: true, message: 'Thickness is required' }, THICKNESS_RULE]}>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={0.5}
                precision={2}
                placeholder="e.g., 5.00"
                parser={TWO_DECIMAL_PARSER}
                formatter={(v) => (v == null ? '' : String(v))}
                onKeyDown={(e) => {
                  // Allow navigation / editing keys + digits + dot. Block
                  // letters, negative sign, exponent notation, etc. so the
                  // field can never accept non-numeric input.
                  const allowed = [
                    'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
                    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                    'Home', 'End'
                  ];
                  if (allowed.includes(e.key)) return;
                  if (e.ctrlKey || e.metaKey) return; // copy/paste/select-all
                  if (/^[0-9]$/.test(e.key)) return;
                  if (e.key === '.' && !e.currentTarget.value.includes('.')) return;
                  e.preventDefault();
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Brand" name="brandId"
              rules={[{ required: true, message: 'Brand is required' }]}>
              <BrandSelect placeholder="Pick from Brand Master…" />
            </Form.Item>
          </Col>

          {/* Row 5 — Category + Item Type (both required) */}
          {/* Hidden mirrors for the picked category's family + color.
              Stashed by the Select onChange below so the helper can read
              them off the form values directly (DB-driven; no hardcoded
              lookup required). */}
          <Form.Item name="glassFamily" hidden><Input /></Form.Item>
          <Form.Item name="glassColor"  hidden><Input /></Form.Item>
          <Col xs={24} md={12}>
            <Form.Item label="Category" name="glassCategoryLabel"
              rules={[{ required: true, message: 'Category is required' }]}>
              <Select
                placeholder="Select category…"
                allowClear
                showSearch
                options={categoryOptions.map((o) => ({ value: o.label, label: o.label }))}
                onChange={(label) => {
                  const picked = categoryOptions.find((o) => o.label === label);
                  form.setFieldValue('glassFamily', picked ? picked.glassFamily : '');
                  form.setFieldValue('glassColor',  picked ? picked.glassColor  : '');
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Item Type" name="itemType"
              rules={[{ required: true, message: 'Item Type is required' }]}>
              <Select
                placeholder="Select item type…"
                allowClear
                showSearch
                options={ITEM_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              />
            </Form.Item>
          </Col>

          {/* Row 6 — Item Category + UOM */}
          <Col xs={24} md={12}>
            <Form.Item label="Item Category" name="itemCategoryId">
              <ItemCategorySelect placeholder="Pick item category…" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="UOM" name="defaultUomId"
              rules={[{ required: true, message: 'UOM is required' }]}>
              <UomSelect placeholder="Select UOM (SQM, PCS, KGS…)" />
            </Form.Item>
          </Col>

          {/* Row 7 — HSN/SAC + GST (%) (both required) */}
          <Col xs={24} md={12}>
            <Form.Item label="HSN / SAC" name="hsncode"
              rules={[
                { required: true, message: 'HSN / SAC is required' },
                { pattern: /^[A-Za-z0-9]+$/, message: 'Letters and digits only' }
              ]}>
              <Input maxLength={12} placeholder="e.g., 7005" />
            </Form.Item>
          </Col>
          {/* gstRatePercent is the snapshot, auto-populated from the
              picked GST rate. Kept as a hidden field so payload contracts
              that read both id + percent continue to work. */}
          <Form.Item name="gstRatePercent" hidden><Input /></Form.Item>
          <Col xs={24} md={12}>
            <Form.Item label="GST (%)" name="gstRateId"
              rules={[{ required: true, message: 'GST rate is required' }]}>
              <GstRateSelect form={form} />
            </Form.Item>
          </Col>

          {/* Row 8 — Selling Price (triple section: Sell / Min / Max)
              + Taxability (both required). Min + Max are stored as
              their own DB columns; the parent Form.Item binds the
              Sell section directly so the existing `required` /
              non-negative rules continue to fire. */}
          <Form.Item name="minSalesRate" hidden><Input /></Form.Item>
          <Form.Item name="maxSalesRate" hidden><Input /></Form.Item>
          <Col xs={24} md={17}>
            <Form.Item
              label="Selling Price"
              name="salesRate"
              rules={[
                { required: true, message: 'Selling Price is required' },
                PRICE_RULE
              ]}
            >
              <TripleSalesRateInput form={form} />
            </Form.Item>
          </Col>
          <Col xs={24} md={7}>
            <Form.Item
              label="Taxability"
              name="taxabilityType"
              labelCol={{ flex: '76px' }}
              wrapperCol={{ flex: 'auto' }}
              rules={[{ required: true, message: 'Taxability is required' }]}
            >
              <Select
                placeholder="Select taxability…"
                options={TAXABILITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              />
            </Form.Item>
          </Col>

          {/* Row 9 — Remarks (full width) */}
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={2} maxLength={500} placeholder="Add any additional notes here..." />
            </Form.Item>
          </Col>
        </>
      )}
    />
  );
}
